import OnBoardingLayout from "../OnBoardingLayout.jsx";
import FormContainer from "../../../../components/form/FormContainer.jsx";
import LoadingComponent from "../../../../components/LoadingComponent.jsx";
import useDownloadState from "../../../../api/utilities/useDownloadState.js";
import {useContext, useEffect, useState} from "react";
import FormControl from "../../../../components/form/FormControl.jsx";
import {useTranslation} from "react-i18next";
import FormButton from "../../../../components/form/FormButton.jsx";
import {GlobalContext} from "../../../../utils/context/GlobalContextProvider.jsx";
import {ProjectApi, UpgatesApi} from "../../../../api/authority/generated/index.ts";
import FormError from "../../../../components/form/FormError.jsx";
import {useNavigate} from "react-router-dom";
import {getLocalStorageUpgatesPendingToken, setLocalStorageUpgatesPendingToken} from "../../../../utils/localStorage.js";

export default function AddonProjectStep() {
    let navigate = useNavigate();
    let {t} = useTranslation();
    let {
        context,
        getAuthorityConfiguration,
        setCurrentProjectId,
        addCurrentUserProject,
        resetCurrentUserProjects,
    } = useContext(GlobalContext);

    let [selectedProjectId, setSelectedProjectId] = useState('');
    let [newProjectName, setNewProjectName] = useState('');
    let [createNewProject, setCreateNewProject] = useState(false);
    let [pendingNavigateProjectId, setPendingNavigateProjectId] = useState(null);

    let [state, setDownloading, , setError] = useDownloadState();
    let [submitPressed, setSubmitPressed] = useState(false);
    const selectableProjects = context.currentUserProjects.filter(
        (project) => !project.hasErpIntegration && !project.hasLeadIntegration
    );
    const hasAvailableProjects = selectableProjects.length > 0;
    const clearPendingTokenAndRedirectHome = (flashMessage = null) => {
        if (flashMessage) {
            sessionStorage.setItem('_flashErrorMessage', flashMessage);
        }
        localStorage.removeItem('_upgatesPendingToken');
        setLocalStorageUpgatesPendingToken(null);
        window.location.assign('/');
    };

    useEffect(() => {
        if (!submitPressed) {
            return;
        }

        setSubmitPressed(false);
        setDownloading(true);

        const pendingToken = getLocalStorageUpgatesPendingToken();
        if (!pendingToken) {
            clearPendingTokenAndRedirectHome();
            return;
        }

        let projectIdToUse = selectedProjectId;

        if (createNewProject && newProjectName.trim() !== '') {
            new ProjectApi(getAuthorityConfiguration())
                .createProject({
                    projectInput: {
                        name: newProjectName.trim()
                    }
                })
                .then((newProject) => {
                    projectIdToUse = newProject.id;
                    addCurrentUserProject(newProject, context.currentIdentity?.token ?? null);
                    provisionAddon(pendingToken, projectIdToUse);
                })
                .catch((e) => {
                    setError(e);
                    setDownloading(false);
                });

            return;
        }

        if (hasAvailableProjects && selectedProjectId) {
            provisionAddon(pendingToken, projectIdToUse);
            return;
        }

        setError(new Error(t('onboarding.select-or-create-project')));
        setDownloading(false);
    }, [
        context.currentIdentity,
        createNewProject,
        getAuthorityConfiguration,
        newProjectName,
        hasAvailableProjects,
        selectedProjectId,
        submitPressed,
        t,
    ]);

    useEffect(() => {
        if (pendingNavigateProjectId === null || !context.currentUserProjectsLoaded) {
            return;
        }

        setCurrentProjectId(pendingNavigateProjectId);
        setPendingNavigateProjectId(null);
        navigate('/');
    }, [context.currentUserProjectsLoaded, navigate, pendingNavigateProjectId, setCurrentProjectId]);

    const provisionAddon = (token, projectId) => {
        new UpgatesApi(getAuthorityConfiguration())
            .upgatesAddonProvisionProject({
                upgatesAddonProvisionProjectRequest: {
                    token: token,
                    projectId: projectId
                },
            })
            .then((response) => {
                if (response?.message === 'token_not_found') {
                    clearPendingTokenAndRedirectHome(t('auth.token-not-found'));
                    return;
                }

                if (response?.error) {
                    setError(new Error(response.message ?? 'provision_failed'));
                    setDownloading(false);
                    return;
                }

                setLocalStorageUpgatesPendingToken(null);
                resetCurrentUserProjects();
                setPendingNavigateProjectId(projectId);
            })
            .catch(async (e) => {
                const errorResponseBody = await e?.response?.clone?.().json?.().catch(() => null);

                if (errorResponseBody?.message === 'token_not_found') {
                    clearPendingTokenAndRedirectHome(t('auth.token-not-found'));
                    return;
                }

                setError(e);
                setDownloading(false);
            });
    };

    return (
        <OnBoardingLayout title={!hasAvailableProjects ? t('project.create') : undefined}>
            <FormContainer>
                <LoadingComponent loading={state.downloading || submitPressed}>
                    {state.error && <FormError error={state.error}/>}

                    {hasAvailableProjects && (
                        <div className="mb-6">
                            <label className="block text-sm font-medium mb-2">
                                {t('onboarding.select-project-for-upgates')}
                            </label>
                            <select
                                value={selectedProjectId}
                                onChange={(event) => {
                                    setSelectedProjectId(event.target.value);
                                    setCreateNewProject(false);
                                }}
                                className="w-full rounded-lg border-2 border-transparent bg-white px-4 py-4 text-sm font-medium leading-[22px] text-slate-800 shadow-sm transition duration-150 ease-in-out hover:border-slate-200 focus:border-[#8470ff] focus:outline-none focus:ring-0"
                            >
                                <option value="">{t('onboarding.choose-project')}</option>
                                {selectableProjects.map((project) => (
                                    <option key={project.id} value={project.id}>
                                        {project.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className={hasAvailableProjects ? 'border-t pt-6' : ''}>
                        <FormControl
                            type={'text'}
                            label={t(hasAvailableProjects ? 'onboarding.or-create-new-project' : 'project.name')}
                            value={newProjectName}
                            onChange={(event) => {
                                setNewProjectName(event.target.value);

                                if (event.target.value.trim() !== '') {
                                    setCreateNewProject(true);
                                    setSelectedProjectId('');
                                }
                            }}
                            placeholder={t('onboarding.enter-new-project-name')}
                            variant={'onboarding'}
                        />
                    </div>

                    <FormButton
                        className={'button-primary mt-6'}
                        onClick={() => setSubmitPressed(true)}
                        disabled={!selectedProjectId && (!createNewProject || newProjectName.trim() === '')}
                    >
                        {t('onboarding.connect-addon')}
                    </FormButton>
                </LoadingComponent>
            </FormContainer>
        </OnBoardingLayout>
    );
}
